import network from '@common/network/network';

import {normalizeManualResearchClient} from './normalizers';
import {getResearchNotesCacheEntry, setResearchNotesCacheEntry} from './notesCacheDb';

const getResearchNotesClientKey = ({client_id, viewer_id, tag, author_id, project_id, is_generated} = {}) => {
  const scopeKey = client_id ? `client:${client_id}` : `all:${viewer_id || 'unknown'}`;
  const filterKeys = [
    tag ? `tag:${tag}` : '',
    author_id ? `author:${author_id}` : '',
    project_id ? `project:${project_id}` : '',
    typeof is_generated === 'boolean' ? `generated:${is_generated}` : '',
  ].filter(Boolean);
  return filterKeys.length ? `${scopeKey}|${filterKeys.join('|')}` : scopeKey;
};

const getResearchNotesFilterOptionsScopeKey = ({client_id, member_id} = {}) => {
  if (client_id) {
    return `client:${client_id}`;
  }
  if (member_id) {
    return `member:${member_id}`;
  }
  return 'current-user';
};

const getResearchNotesAssetsScopeKey = ({client_id} = {}) => (client_id ? `client:${client_id}` : 'default');
const getResearchListScopeKey = ({client_id, scope_key} = {}) => client_id || scope_key || 'personal';

const normalizeResearchClientSharepointFiles = response => {
  const objects = Array.isArray(response?.objects) ? response.objects : [];
  let filesCount = objects.length;
  if (Number.isFinite(response?.files_count) && response.files_count >= 0) {
    filesCount = response.files_count;
  }

  return {
    objects,
    folder_path: response?.folder_path || '',
    sharepoint_url: response?.sharepoint_url || '',
    files_count: filesCount,
  };
};

const normalizeResearchClientArchiveFiles = response => {
  const objects = Array.isArray(response?.objects) ? response.objects : [];
  let filesCount = objects.length;
  if (Number.isFinite(response?.files_count) && response.files_count >= 0) {
    filesCount = response.files_count;
  }

  return {
    objects,
    archive_bucket: response?.archive_bucket || '',
    archive_prefix: response?.archive_prefix || '',
    folder_path: response?.folder_path || '',
    files_count: filesCount,
  };
};

export default {
  showResearchNotesPageAction(
    context,
    {client_id, viewer_id, tag, author_id, project_id, is_generated, page = 1} = {}
  ) {
    const memberKey = getResearchNotesClientKey({client_id, viewer_id, tag, author_id, project_id, is_generated});
    context.commit('setActiveResearchNotesMemberIdState', memberKey);
    context.commit('clearActiveResearchNotesState');
    context.commit('setResearchNotesLoadingState', true);
    return getResearchNotesCacheEntry(memberKey).then(cachedPage => {
      if (cachedPage) {
        context.commit('setResearchNotesPageState', {
          notes: cachedPage.notes,
          meta: cachedPage.meta,
        });
        return context.dispatch('fetchResearchNotesAction', {
          client_id,
          viewer_id,
          tag,
          author_id,
          project_id,
          is_generated,
          page,
        });
      }

      return context.dispatch('fetchResearchNotesAction', {
        client_id,
        viewer_id,
        tag,
        author_id,
        project_id,
        is_generated,
        page,
      });
    });
  },
  fetchResearchNotesAction(
    context,
    {client_id, viewer_id, tag, author_id, project_id, is_generated, page, clearExisting = false} = {}
  ) {
    const requestId = (context.state.notesLoadingRequestId || 0) + 1;
    const memberKey = getResearchNotesClientKey({client_id, viewer_id, tag, author_id, project_id, is_generated});
    context.commit('setActiveResearchNotesMemberIdState', memberKey);
    context.commit('setResearchNotesLoadingRequestId', requestId);
    context.commit('setResearchNotesLoadingState', true);
    if (clearExisting) {
      context.commit('clearActiveResearchNotesState');
    }
    page = page || 1;
    const limit = 20;
    const offset = limit * (page - 1);

    return new Promise((resolve, reject) => {
      const params = {offset, limit};
      if (client_id) {
        params.client_id = client_id;
      }
      if (tag) {
        params.tags = tag;
      }
      if (author_id) {
        params.author_id = author_id;
      }
      if (project_id) {
        params.project_id = project_id;
      }
      if (typeof is_generated === 'boolean') {
        params.is_generated = is_generated;
      }

      network.research
        .getResearchNotes(params)
        .then(response => {
          setResearchNotesCacheEntry(memberKey, {
            notes: response.objects,
            meta: response.meta,
          });
          context.commit('setResearchNotesState', response.objects);
          context.commit('setResearchNotesMetaState', response.meta);
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          if (context.state.notesLoadingRequestId === requestId) {
            context.commit('setResearchNotesLoadingState', false);
          }
        });
    });
  },
  fetchResearchNotesFilterOptionsAction(context, {client_id, member_id} = {}) {
    const scopeKey = getResearchNotesFilterOptionsScopeKey({client_id, member_id});
    const params = {};
    if (client_id) {
      params.client_id = client_id;
    } else if (member_id) {
      params.member_id = member_id;
    }

    return network.research.getResearchNotesOptions(params).then(response => {
      const nextOptions = {
        tags: Array.isArray(response?.tags) ? response.tags : [],
        authors: Array.isArray(response?.authors) ? response.authors : [],
        projects: Array.isArray(response?.projects) ? response.projects : [],
      };
      context.commit('setResearchNotesFilterOptionsForScopeState', {
        scopeKey,
        ...nextOptions,
      });
      return nextOptions;
    });
  },
  fetchResearchNotesAssetsAction(context, {client_id} = {}) {
    const scopeKey = getResearchNotesAssetsScopeKey({client_id});
    const params = {};
    if (client_id) {
      params.client_id = client_id;
    }

    context.commit('setResearchNotesAssetsLoadingForScopeState', {scopeKey, isLoading: true});
    context.commit('setResearchNotesAssetsErrorForScopeState', {scopeKey, error: null});

    return network.research
      .getResearchNotesAssets(params)
      .then(response => {
        const data = {
          objects: Array.isArray(response?.objects) ? response.objects : Array.isArray(response) ? response : [],
        };
        context.commit('setResearchNotesAssetsForScopeState', {
          scopeKey,
          data,
        });
        return data;
      })
      .catch(error => {
        context.commit('setResearchNotesAssetsErrorForScopeState', {scopeKey, error});
        throw error;
      })
      .finally(() => {
        context.commit('setResearchNotesAssetsLoadingForScopeState', {scopeKey, isLoading: false});
      });
  },
  fetchResearchMembersAction(context, {member_id, active} = {}) {
    return network.research
      .getResearchMembers({member_id, active})
      .then(response => {
        context.commit('setResearcherClientsState', {
          memberships: response?.objects || response,
        });
        return response;
      })
      .catch(error => {
        context.commit('setResearcherClientsState', {
          memberships: [],
        });
        throw error;
      });
  },
  searchResearchMembersAction(context, {query}) {
    return network.research.searchResearchMembers({query}).then(response => {
      const results = Array.isArray(response?.objects) ? response.objects : Array.isArray(response) ? response : [];
      return results;
    });
  },
  fetchResearchClientAction(context, {id, commitToState = false} = {}) {
    return network.research.getResearchClient({id}).then(response => {
      const client = normalizeManualResearchClient(response);
      if (!client) {
        return client;
      }
      return network.research
        .getResearchClientRelatedClients({id})
        .catch(() => ({objects: []}))
        .then(relatedResponse => {
          let relatedClients = [];
          if (Array.isArray(relatedResponse?.objects)) {
            relatedClients = relatedResponse.objects;
          } else if (Array.isArray(relatedResponse)) {
            relatedClients = relatedResponse;
          }
          client.related_clients = relatedClients.map(normalizeManualResearchClient).filter(Boolean);
          if (commitToState && client?.id) {
            context.commit('upsertResearchClientState', client);
          }
          return client;
        });
    });
  },
  searchResearchClientsAction(context, {query}) {
    return network.research.searchResearchClients({query}).then(response => {
      const results = Array.isArray(response?.objects) ? response.objects : Array.isArray(response) ? response : [];
      return results.map(normalizeManualResearchClient).filter(Boolean);
    });
  },
  createResearchClientAction(context, payload) {
    return network.research.createResearchClient(payload).then(response => normalizeManualResearchClient(response));
  },
  renameResearchClientAction(context, {client_id, name} = {}) {
    const normalizedName = `${name || ''}`.trim();
    if (!client_id || !normalizedName) {
      return Promise.reject(new Error('Missing client or name.'));
    }
    return network.research.updateResearchClient({id: client_id, name: normalizedName}).then(response => {
      const client = normalizeManualResearchClient(response);
      if (client?.id) {
        context.commit('upsertResearchClientState', client);
      }
      return client;
    });
  },
  previewResearchClientMergeAction(_context, {client_ids, primary_client_id, name, main_contact_id} = {}) {
    const payload = {
      client_ids,
    };
    if (primary_client_id) {
      payload.primary_client_id = primary_client_id;
    }
    if (name) {
      payload.name = name;
    }
    if (main_contact_id) {
      payload.main_contact_id = main_contact_id;
    }
    return network.research.previewResearchClientMerge(payload);
  },
  mergeResearchClientsAction(context, {client_ids, primary_client_id, name, main_contact_id} = {}) {
    const payload = {
      client_ids,
      primary_client_id,
      name,
    };
    if (main_contact_id) {
      payload.main_contact_id = main_contact_id;
    }
    return network.research.mergeResearchClients(payload).then(response => {
      const client = normalizeManualResearchClient(response);
      const result = client
        ? {
            ...client,
            sharepoint_relocation_task_id: response?.sharepoint_relocation_task_id || '',
            sharepoint_relocation_status: response?.sharepoint_relocation_status || '',
          }
        : null;
      const duplicateClientIds = (Array.isArray(client_ids) ? client_ids : []).filter(
        clientId => `${clientId}` !== `${primary_client_id}`
      );
      context.commit('removeResearchClientsState', {
        clientIds: duplicateClientIds,
        replacementClientId: primary_client_id,
      });
      if (result?.id) {
        context.commit('upsertResearchClientState', result);
      }
      return result;
    });
  },
  fetchResearchClientSharepointRelocationStatusAction(_context, {client_id, task_id} = {}) {
    if (!client_id || !task_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientSharepointRelocationStatus({
      id: client_id,
      task_id,
    });
  },
  setResearchClientMainContactAction(context, {client_id, contact_id} = {}) {
    if (!client_id || !contact_id) {
      return Promise.reject(new Error('Missing client or contact.'));
    }
    return network.research.setResearchClientMainContact({id: client_id, contact_id}).then(response => {
      const client = normalizeManualResearchClient(response);
      if (client?.id) {
        context.commit('upsertResearchClientState', client);
      }
      return client;
    });
  },
  fetchResearchClientSharepointFilesAction(context, {client_id} = {}) {
    if (!client_id) {
      return Promise.resolve(null);
    }

    context.commit('setResearchSharepointFilesLoadingForClientState', {clientId: client_id, isLoading: true});
    context.commit('setResearchSharepointFilesErrorForClientState', {clientId: client_id, error: null});

    return network.research
      .getResearchClientSharepointFiles({id: client_id})
      .then(response => {
        const data = normalizeResearchClientSharepointFiles(response);
        context.commit('setResearchSharepointFilesForClientState', {
          clientId: client_id,
          data: {
            ...data,
            fetched_at: Date.now(),
          },
        });
        return data;
      })
      .catch(error => {
        context.commit('setResearchSharepointFilesErrorForClientState', {clientId: client_id, error});
        throw error;
      })
      .finally(() => {
        context.commit('setResearchSharepointFilesLoadingForClientState', {clientId: client_id, isLoading: false});
      });
  },
  fetchResearchClientArchiveFilesAction(context, {client_id, prefix} = {}) {
    if (!client_id) {
      return Promise.resolve(null);
    }

    context.commit('setResearchArchiveFilesLoadingForClientState', {clientId: client_id, isLoading: true});
    context.commit('setResearchArchiveFilesErrorForClientState', {clientId: client_id, error: null});

    const params = {id: client_id};
    if (prefix) {
      params.prefix = prefix;
    }

    return network.research
      .getResearchClientArchiveFiles(params)
      .then(response => {
        if (response?.error) {
          throw response;
        }
        const data = normalizeResearchClientArchiveFiles(response);
        context.commit('setResearchArchiveFilesForClientState', {
          clientId: client_id,
          data: {
            ...data,
            fetched_at: Date.now(),
          },
        });
        return data;
      })
      .catch(error => {
        context.commit('setResearchArchiveFilesErrorForClientState', {clientId: client_id, error});
        throw error;
      })
      .finally(() => {
        context.commit('setResearchArchiveFilesLoadingForClientState', {clientId: client_id, isLoading: false});
      });
  },
  fetchResearchClientArchiveFileDownloadUrlAction(_context, {client_id, archive_key, open, download} = {}) {
    if (!client_id || !archive_key) {
      return Promise.resolve(null);
    }

    return network.research.getResearchClientArchiveFileDownloadUrl({
      id: client_id,
      archive_key,
      open,
      download,
    });
  },
  fetchResearchSharepointFileDownloadUrlAction(_context, {file_id, download} = {}) {
    if (!file_id) {
      return Promise.resolve(null);
    }

    return network.research.getResearchSharepointFileDownloadUrl({
      file_id,
      download,
    });
  },
  createResearchSharepointZipAction(_context, {file_ids, archive_name} = {}) {
    if (!Array.isArray(file_ids) || !file_ids.length) {
      return Promise.resolve(null);
    }
    return network.research.createResearchSharepointZip({file_ids, archive_name});
  },
  createResearchArchiveZipAction(_context, {archive_keys, archive_name} = {}) {
    if (!Array.isArray(archive_keys) || !archive_keys.length) {
      return Promise.resolve(null);
    }
    return network.research.createResearchArchiveZip({archive_keys, archive_name});
  },
  fetchResearchClientSharepointFolderOptionsAction(_context, {client_id} = {}) {
    if (!client_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientSharepointFolderOptions({id: client_id});
  },
  saveResearchClientSharepointFolderOptionAction(_context, {client_id, sharepoint_folder} = {}) {
    if (!client_id || !sharepoint_folder) {
      return Promise.resolve(null);
    }
    return network.research.saveResearchClientSharepointFolderOption({
      id: client_id,
      sharepoint_folder,
    });
  },
  fetchResearchClientArchiveFolderOptionsAction(_context, {client_id} = {}) {
    if (!client_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientArchiveFolderOptions({id: client_id});
  },
  fetchResearchArchiveExploreAction(_context, {prefix} = {}) {
    const payload = {};
    if (prefix) {
      payload.prefix = prefix;
    }
    return network.research.getResearchArchiveExplore(payload);
  },
  fetchResearchClientArchiveSuggestionsAction(_context, {client_id} = {}) {
    if (!client_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientArchiveSuggestions({id: client_id});
  },
  saveResearchClientArchiveFolderOptionAction(_context, {client_id, archive_folder} = {}) {
    if (!client_id || !archive_folder) {
      return Promise.resolve(null);
    }
    return network.research.saveResearchClientArchiveFolderOption({
      id: client_id,
      archive_folder,
    });
  },
  generateResearchProjectArchiveFolderAction(context, {client_id, project_id} = {}) {
    if (!client_id || !project_id) {
      return Promise.resolve(null);
    }
    return network.research
      .generateResearchProjectArchiveFolder({
        id: client_id,
        project_id,
      })
      .then(response => {
        const archiveFolder = `${response?.archive_folder || ''}`.trim();
        if (!archiveFolder) {
          return null;
        }
        const project = {
          id: project_id,
          archive_folder: archiveFolder,
        };
        context.commit('updateResearchProjectArchiveFolderState', {
          projectId: project_id,
          archiveFolder,
        });
        return project;
      });
  },
  generateResearchProjectSharepointFolderAction(context, {client_id, project_id} = {}) {
    if (!client_id || !project_id) {
      return Promise.resolve(null);
    }
    return network.research
      .generateResearchProjectSharepointFolder({
        id: client_id,
        project_id,
      })
      .then(response => {
        const sharepointFolder = `${response?.sharepoint_folder || ''}`.trim();
        if (!sharepointFolder) {
          return null;
        }
        const project = {
          id: project_id,
          sharepoint_folder: sharepointFolder,
        };
        context.commit('updateResearchProjectSharepointFolderState', {
          projectId: project_id,
          sharepointFolder,
        });
        return project;
      });
  },
  bulkCopyResearchArchiveFilesToProjectAction(_context, {client_id, project_id, files} = {}) {
    if (!client_id || !project_id || !Array.isArray(files)) {
      return Promise.resolve(null);
    }
    return network.research.bulkActionResearchClientArchiveFiles({
      id: client_id,
      action: 'copy_archive_files_to_project',
      project_id,
      files,
    });
  },
  fetchResearchArchiveBulkActionStatusAction(_context, {client_id, task_id} = {}) {
    if (!client_id || !task_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientArchiveFilesBulkActionStatus({
      id: client_id,
      task_id,
    });
  },
  bulkCopyResearchSharepointFilesToLibraryAction(_context, {client_id, files} = {}) {
    if (!client_id || !Array.isArray(files)) {
      return Promise.resolve(null);
    }
    return network.research.bulkActionResearchClientSharepointFiles({
      id: client_id,
      action: 'copy_file_to_library',
      files,
    });
  },
  fetchResearchSharepointBulkActionStatusAction(_context, {client_id, task_id} = {}) {
    if (!client_id || !task_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchClientSharepointFilesBulkActionStatus({
      id: client_id,
      task_id,
    });
  },
  bulkCopyResearchNoteAssetsToLibraryAction(_context, {client_id, asset_ids} = {}) {
    if (!client_id || !Array.isArray(asset_ids)) {
      return Promise.resolve(null);
    }
    return network.research.bulkActionResearchNotesAssets({
      action: 'copy_file_to_library',
      client_id,
      asset_ids,
    });
  },
  bulkDeleteResearchNoteAssetsAction(_context, {client_id, asset_ids} = {}) {
    if (!Array.isArray(asset_ids)) {
      return Promise.resolve(null);
    }
    const payload = {
      action: 'delete',
      asset_ids,
    };
    if (client_id) {
      payload.client_id = client_id;
    }
    return network.research.bulkActionResearchNotesAssets(payload);
  },
  fetchResearchNoteAssetsBulkActionStatusAction(_context, {task_id} = {}) {
    if (!task_id) {
      return Promise.resolve(null);
    }
    return network.research.getResearchNotesAssetsBulkActionStatus({
      task_id,
    });
  },
  createResearchMemberAction(context, payload) {
    context.commit('setResearchMembersUpdatingState', true);
    return new Promise((resolve, reject) => {
      network.research
        .createResearchMember(payload)
        .then(response => {
          context.commit('upsertResearchMemberState', response);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setResearchMembersUpdatingState', false);
        });
    });
  },
  updateResearchMemberAction(context, payload) {
    context.commit('setResearchMembersUpdatingState', true);
    return new Promise((resolve, reject) => {
      network.research
        .updateResearchMember(payload)
        .then(response => {
          context.commit('upsertResearchMemberState', response);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setResearchMembersUpdatingState', false);
        });
    });
  },
  deleteResearchMemberAction(context, {id, clientId}) {
    context.commit('setResearchMembersUpdatingState', true);
    return new Promise((resolve, reject) => {
      network.research
        .deleteResearchMember({id})
        .then(response => {
          context.commit('removeResearchMemberState', clientId);
          resolve(response);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setResearchMembersUpdatingState', false);
        });
    });
  },
  fetchResearchClientProjectsAction(context, {client_id, scope_key} = {}) {
    const scopeKey = getResearchListScopeKey({client_id, scope_key});

    context.commit('setResearchProjectsLoadingForClientState', {clientId: scopeKey, isLoading: true});
    context.commit('setResearchProjectsErrorForClientState', {clientId: scopeKey, error: null});

    return network.research
      .getResearchClientProjects(client_id ? {client_id} : {})
      .then(response => {
        const data = {
          objects: Array.isArray(response?.objects) ? response.objects : Array.isArray(response) ? response : [],
        };
        context.commit('setResearchProjectsForClientState', {
          clientId: scopeKey,
          data: {
            ...data,
            fetched_at: Date.now(),
          },
        });
        return data;
      })
      .catch(error => {
        context.commit('setResearchProjectsErrorForClientState', {clientId: scopeKey, error});
        throw error;
      })
      .finally(() => {
        context.commit('setResearchProjectsLoadingForClientState', {clientId: scopeKey, isLoading: false});
      });
  },
  fetchResearchClientSkillSessionsAction(context, {client_id, scope_key} = {}) {
    const scopeKey = getResearchListScopeKey({client_id, scope_key});

    context.commit('setResearchSkillSessionsLoadingForClientState', {clientId: scopeKey, isLoading: true});
    context.commit('setResearchSkillSessionsErrorForClientState', {clientId: scopeKey, error: null});

    return network.research
      .getResearchClientSkillSessions(client_id ? {client_id} : {})
      .then(response => {
        const data = {
          objects: Array.isArray(response?.objects) ? response.objects : Array.isArray(response) ? response : [],
        };
        context.commit('setResearchSkillSessionsForClientState', {
          clientId: scopeKey,
          data: {
            ...data,
            fetched_at: Date.now(),
          },
        });
        return data;
      })
      .catch(error => {
        context.commit('setResearchSkillSessionsErrorForClientState', {clientId: scopeKey, error});
        throw error;
      })
      .finally(() => {
        context.commit('setResearchSkillSessionsLoadingForClientState', {clientId: scopeKey, isLoading: false});
      });
  },
  fetchResearchNoteAction(context, {id}) {
    context.commit('setResearchNoteLoadingState', true);

    return new Promise((resolve, reject) => {
      network.research
        .getResearchNote({id})
        .then(response => {
          context.commit('setResearchNoteState', response);
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setResearchNoteLoadingState', false);
        });
    });
  },

  createResearchNoteAction(context, payload) {
    context.commit('setResearchNoteLoadingState', true);

    return new Promise((resolve, reject) => {
      network.research
        .createResearchNote(payload)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setResearchNoteLoadingState', false);
        });
    });
  },
  updateResearchNoteAction(context, payload) {
    context.commit('setResearchNoteUpdateLoadingState', true);

    return new Promise((resolve, reject) => {
      network.research
        .updateResearchNote(payload)
        .then(response => {
          context.commit('setResearchNoteState', response);
          context.commit('mutateResearchNotesState', {
            noteId: response.id,
            title: response.title,
            content: response.content,
            content_json: response.content_json,
            location: response.location,
            tags: response.tags,
            projects: response.projects,
          });
          context.commit('mutateResearchUnifiedSearchItemState', {...response, kind: 'research_note'});
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setResearchNoteUpdateLoadingState', false);
        });
    });
  },
  bulkMoveResearchNotesAction(_context, payload) {
    return network.research.bulkMoveResearchNotes(payload);
  },
  bulkTagResearchNotesAction(_context, payload) {
    return network.research.bulkTagResearchNotes(payload);
  },
  dissolveResearchLegacyProjectAction(_context, {legacy_project_id, project_ids} = {}) {
    const normalizedLegacyProjectId = `${legacy_project_id || ''}`.trim();
    const normalizedProjectIds = (Array.isArray(project_ids) ? project_ids : [])
      .map(projectId => `${projectId || ''}`.trim())
      .filter(Boolean);
    if (!normalizedLegacyProjectId || !normalizedProjectIds.length) {
      return Promise.reject(new Error('Missing legacy project or destination projects.'));
    }
    return network.research.dissolveResearchLegacyProject({
      legacy_project_id: normalizedLegacyProjectId,
      project_ids: normalizedProjectIds,
    });
  },
  moveResearchClientProjectsAction(_context, {target_client_id, project_ids} = {}) {
    const normalizedTargetClientId = `${target_client_id || ''}`.trim();
    const normalizedProjectIds = (Array.isArray(project_ids) ? project_ids : [])
      .map(projectId => `${projectId || ''}`.trim())
      .filter(Boolean);
    if (!normalizedTargetClientId || !normalizedProjectIds.length) {
      return Promise.reject(new Error('Missing target client or projects.'));
    }
    return network.research.moveResearchClientProjects({
      target_client_id: normalizedTargetClientId,
      project_ids: normalizedProjectIds,
    });
  },
  deleteResearchNoteAction(context, {id}) {
    context.commit('setResearchNoteUpdateLoadingState', true);

    return new Promise((resolve, reject) => {
      network.research
        .deleteResearchNote({id})
        .then(response => {
          context.commit('removeResearchNoteState', id);
          context.commit('clearResearchNoteState', id);
          context.commit('removeResearchUnifiedSearchItemState', id);
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setResearchNoteUpdateLoadingState', false);
        });
    });
  },

  uploadResearchNoteAssetAction(context, {id, file, onProgress}) {
    return new Promise((resolve, reject) => {
      network.research
        .uploadNoteAsset({id, attachment: file}, progressRes => {
          const value = ((progressRes.loaded / progressRes.total) * 100).toFixed(2);
          onProgress?.(value);
          console.log(`Upload progress: ${value}%`);
        })
        .then(response => {
          context.commit('mutateResearchNoteAssetsState', response);
          resolve(response);
        })
        .catch(error => {
          console.log('catch in action');
          reject(error);
        });
    });
  },
  uploadResearchNoteAssetFromUrlAction(context, {id, url}) {
    return network.research.uploadNoteAssetFromUrl({id, url}).then(response => {
      context.commit('mutateResearchNoteAssetsState', response);
      return response;
    });
  },
  fetchResearchUnifiedSearchAction(
    context,
    {
      page,
      type,
      query,
      contact,
      contact_id,
      member_id,
      hubspot_contact_id,
      author_id,
      tags,
      research_project_id,
      category,
      note_type,
      clauses,
    }
  ) {
    page = page || 1;
    const limit = 10;
    const offset = limit * (page - 1);
    const params = {
      query,
      limit,
      offset,
    };

    if (type) {
      params.type = type;
    }
    if (author_id) {
      params.author_id = author_id;
    }
    if (contact) {
      params.contact = contact;
    }
    if (contact_id) {
      params.contact_id = contact_id;
    }
    if (member_id) {
      params.member_id = member_id;
    }
    if (hubspot_contact_id) {
      params.hubspot_contact_id = hubspot_contact_id;
    }
    if (Array.isArray(tags) && tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (research_project_id) {
      params.research_project_id = research_project_id;
    }
    if (category) {
      params.category = category;
    }
    if (note_type) {
      params.note_type = note_type;
    }
    if (clauses) {
      params.clauses = clauses;
    }

    context.commit('setResearchUnifiedSearchLoadingState', true);
    return new Promise((resolve, reject) => {
      network.fws
        .searchResearchContent(params)
        .then(response => {
          context.commit('setResearchUnifiedSearchListState', response.objects || []);
          context.commit('setResearchUnifiedSearchMetaState', response.meta || {});
          resolve(response);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setResearchUnifiedSearchLoadingState', false);
        });
    });
  },
  fetchResearchUnifiedSearchOptionsAction(context, {projects_query, project_limit, tag_limit} = {}) {
    return new Promise((resolve, reject) => {
      network.fws
        .getResearchContentOptions({
          projects_query,
          project_limit,
          tag_limit,
        })
        .then(resolve)
        .catch(error => {
          reject(error);
        });
    });
  },
  fetchResearchUnifiedSearchContactsAutocompleteAction(context, {query, limit} = {}) {
    return new Promise((resolve, reject) => {
      network.fws
        .autocompleteResearchContentContacts({
          query,
          limit,
        })
        .then(resolve)
        .catch(error => {
          reject(error);
        });
    });
  },
};
